import { action, observable, runInAction } from 'mobx';
import { Container, Inject } from 'typescript-ioc';
import { AbstractFormViewModelController } from '../../../../../../../../../../infrastructure/implementations/form-viewmodel-controller';
import { IErrorParsing } from '../../../../../../../../../../infrastructure/error-parsing';
import { alertServerError } from '../../../../../../../../../global/modal/alerts-host/alert-templates';
import { errorText } from '../../../../../../../../../../constants/common-labels.constants';
import { IAccountOrganizationDTO, IAccountOrganizationsDAO, ICreateAccountOrganizationDTO } from '../../../../../../../../../../api/account-organizations.dao';
import { AccountOrganizationsDAO } from '../../../../../../../../../../api/implementations/account-organizations.dao';
import { buildSpeakerOrganizationFormConfig, NAME } from './speaker-organization-form.config';
import { HTTP_STATUS_CODE } from '../../../../../../../../../../infrastructure/api-client';

export class SpeakerOrganizationFormVM extends AbstractFormViewModelController {
    @observable
    public isSaving: boolean = false;
    @observable
    public serverErrorMessage: string = '';

    constructor(
        private readonly accountId: number,
        existingOrganizationNames: string[],
        private readonly anchorElement: HTMLDivElement | null,
        private readonly onOrganizationCreated: (organization: IAccountOrganizationDTO) => void,
        private readonly onCancelCallback: () => void,
        @Inject private readonly accountOrganizationsDAO: IAccountOrganizationsDAO = Container.get(AccountOrganizationsDAO),
        @Inject private readonly errorParsingService: IErrorParsing = Container.get(IErrorParsing),
    ) {
        super({}, 'SpeakerOrganizationFormVM', buildSpeakerOrganizationFormConfig(existingOrganizationNames));
    }

    public getAnchorElement = (): HTMLDivElement | null => {
        return this.anchorElement;
    };

    @action
    public clearServerError = (): void => {
        this.serverErrorMessage = '';
    };

    public cancel = (): void => {
        this.onCancelCallback();
    };

    public submit = async (): Promise<void> => {
        this.setVmTouched();
        if (!this.isVmValid()) return;

        await this.createOrganization();
    };

    @action
    private async createOrganization(): Promise<void> {
        this.isSaving = true;
        try {
            const organization = await this.accountOrganizationsDAO.createAccountOrganization(this.accountId, this.toCreateAccountOrganizationDTO());
            this.onOrganizationCreated(organization);
        } catch (error) {
            const apiError = this.errorParsingService.extractAPIError(error);
            if (apiError?.errorCode === HTTP_STATUS_CODE.CONFLICT) {
                runInAction(() => {
                    this.serverErrorMessage = apiError.errorMessage;
                });
            } else {
                const message = this.errorParsingService.extractMessage(error);
                alertServerError(errorText(), message);
            }
        } finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    }

    private toCreateAccountOrganizationDTO(): ICreateAccountOrganizationDTO {
        return {
            name: (this.getFormVmValuePropsByKey(NAME).value as string).trim()
        };
    }
}
